import { keyframes, styled } from '@mui/material/styles';
import { RAIL_BREAKPOINT, TAG_RAIL_W } from '../features/explore/EventList';
import { TOGGLE_BOTTOM_CSS } from './viewToggleMetrics';
import {
  LOCKUP_BY,
  LOCKUP_PER_WORDMARK,
  STRIP_X,
  TOGGLE_GAP,
  WORDMARK_W,
} from '../shared/utils/constants';

/**
 * The page from 900px up: three full-height columns and no bar across the top.
 * The left sidebar holds the filters (Explore only), the centre column the list
 * or the week, and the right sidebar whatever the view puts there. Below 900px
 * none of this is used; the header and the page swipe are unchanged.
 */

/** Both sidebars' ground. Flat: the paper texture belongs to the filter panel below 900px. */
export const SIDEBAR_GROUND = '#2e2925';

/**
 * The shadow each sidebar casts onto the page beside it, as though it sat a
 * little above it: darkest at the sidebar's edge. 4px on an 1800px screen, and
 * in proportion on others (Sam, Sept 18).
 */
export const SIDEBAR_SHADOW_W = 'calc(100vw * 4 / 1800)';
export const sidebarShadow = (towards: 'left' | 'right') =>
  `linear-gradient(to ${towards}, #312826, rgba(49, 40, 38, 0))`;

/**
 * The row along the top of every column. The lockup, the view toggle and About
 * Us / Submit Feedback share one line across the three, whichever column each
 * has landed in, so every column's top is this tall: the header's height.
 */
export const TOP_STRIP_H = 79;

/**
 * The filters' share of the page until it is dragged, again on a double-click,
 * and when the right sidebar opens beside filters dragged too wide for it. On a
 * page wide enough for the right sidebar to start open, the share measured off
 * the Sept 17 mocks; narrower, the golden ratio the sidebar has always had.
 * Set by the page rather than by the right sidebar, so that closing it does not
 * move the filters.
 */
export const LEFT_FRACTION = { wide: 0.274, narrow: 0.382 };

/** The drag handle between the filters and the list. */
export const HANDLE_W = 12;

/**
 * The narrowest a ticket may be drawn from 900px up, measured off Sam's
 * screenshot of Sept 18: narrower, its title and place stop reading. The
 * filters stop there when dragged, and give way to the right sidebar for it.
 */
export const TICKET_MIN_W = 470;

/** The right sidebar's width on a given page: the clamp in its CSS, in numbers. */
export const rightSidebarWidth = (pageW: number) => Math.min(380, Math.max(300, 0.2 * pageW));

/**
 * The narrowest the list's column may be: a minimum ticket, its four buttons
 * (30, and 8 between), the tag rail on a page wide enough to have one, the
 * list's side padding (22 and 18), and its scrollbar where one takes room.
 */
export const listMinWidth = (pageW: number, scrollbar: number) =>
  TICKET_MIN_W + 38 + (pageW >= RAIL_BREAKPOINT ? TAG_RAIL_W : 0) + 40 + scrollbar;

/**
 * Below this the right sidebar starts closed. Three columns at 920px leave an
 * event card about 310px, narrower than it has ever been; the toggle still
 * opens it at any width.
 */
export const RIGHT_OPEN_FROM = 1280;

export const CentreColumn = styled('div')({
  position: 'relative',
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
});

/**
 * Where the lockup in the top strip ends, in pixels: the same clamp
 * `LOCKUP_IN_STRIP` writes in CSS, in numbers. It is anchored to the screen's
 * centre, never to a toggle already pushed right — feeding one into the other
 * only makes them chase each other.
 */
export const stripLockupRight = (pageW: number, toggleW: number) => {
  const wordmark = Math.min(
    WORDMARK_W.wide,
    Math.max(114, (pageW / 2 - (STRIP_X + TOGGLE_GAP + LOCKUP_BY) - toggleW / 2) / LOCKUP_PER_WORDMARK),
  );
  return STRIP_X + wordmark * LOCKUP_PER_WORDMARK + LOCKUP_BY;
};

/**
 * Where the view toggle's centre sits, from 900px up: the middle of the screen,
 * in both views. It is drawn once, over the page, rather than in each view's
 * strip, where it was centred over a different column in each and jumped on
 * every switch. Filters dragged wide enough to reach it push it right, and it
 * stays pushed on My Schedule too, so that switching views still never moves it.
 *
 * Below 680 + T the smallest lockup the strip will draw reaches a centred pill,
 * so the toggle is pushed clear of that too — in both views, whichever is
 * showing, or it would move on a switch.
 *
 * `filtersEdge` is where Explore's filters rest, handle included: zero when they
 * are closed. It is deliberately their resting edge and not their live one. A
 * callout opening beside the list steps them aside and collapses the live edge,
 * and a toggle pushed off that would jump left as it opened.
 */
export const viewToggleX = (pageW: number, filtersEdge: number, toggleW: number) =>
  Math.max(
    pageW / 2,
    filtersEdge + TOGGLE_GAP + toggleW / 2,
    stripLockupRight(pageW, toggleW) + TOGGLE_GAP + toggleW / 2,
  );

/** The callout's width from 900px up, whether it is the right sidebar or drawn over the list. */
export const CALLOUT_W = 460;

/**
 * Can the callout be the right sidebar? Only beside a list whose tickets still
 * read, and clear of the view toggle, which stays at the screen's centre.
 * Otherwise it is drawn over the list (Sam, Sept 18): below about 952px plus
 * the pill's width, where the toggle would sit on the callout's header — 1,212px
 * signed in. `filtersEdge` is where the filters end beside it, zero when they
 * have stepped aside.
 */
export const calloutFitsBeside = (
  pageW: number,
  scrollbar: number,
  filtersEdge: number,
  toggleX: number,
  toggleW: number,
) =>
  pageW - CALLOUT_W >=
  Math.max(filtersEdge + listMinWidth(pageW, scrollbar), toggleX + toggleW / 2 + TOGGLE_GAP);

/**
 * What the right sidebar holds, from 900px up, in either view. The one place
 * this is decided: a callout beside the list beats the view's own panel
 * (Explore's day, My Schedule's totals), and holds the sidebar open whether or
 * not the panel was, so closing it puts back whichever the user had.
 */
export const rightSidebarHolds = (callout: boolean, panelOpen: boolean): 'callout' | 'panel' | null =>
  callout ? 'callout' : panelOpen ? 'panel' : null;

/**
 * The view toggle, from 900px up: level with the top strip, at `viewToggleX`.
 *
 * `$over` is the account page, which paints at 1260 over everything App draws.
 * Only then does the toggle need to beat it; the rest of the time it stays at 9,
 * under the callout Drawer that is allowed to cover it on `/`.
 */
export const ToggleDock = styled('div', { shouldForwardProp: (p) => p !== '$over' })<{
  $over?: boolean;
}>(({ $over }) => ({
  position: 'fixed',
  top: 0,
  height: TOP_STRIP_H,
  display: 'flex',
  alignItems: 'center',
  transform: 'translateX(-50%)',
  // Over the columns' tops, under a drawer or a dialog.
  zIndex: $over ? 1270 : 9,
}));

/**
 * The view toggle on a phone: pinned to the bottom of the screen, over
 * everything the page draws.
 *
 * 1270 puts it above the callout sheets (1200, scrim 1199), the filter panel
 * (1150, scrim 1149), the day view, the toast and the account page (1260), and
 * below MUI's modals (1300), which are meant to cover it. 18.4px is measured to
 * the physical bottom of the screen, so a home-indicator inset replaces it
 * rather than adding to it.
 */
export const PhoneToggleDock = styled('div')({
  position: 'fixed',
  left: '50%',
  bottom: TOGGLE_BOTTOM_CSS,
  transform: 'translateX(-50%)',
  zIndex: 1270,
});

/**
 * The centre column's top: the lockup at its left when there is no left
 * sidebar to hold it, and About Us / Submit Feedback at its right when there is
 * no right sidebar. The view toggle is drawn over it by the page, and both ends
 * keep clear of it.
 */
export const TopStrip = styled('div')({
  flexShrink: 0,
  height: TOP_STRIP_H,
  display: 'flex',
  alignItems: 'center',
  gap: TOGGLE_GAP,
  padding: `0 ${STRIP_X}px`,
});

const arrive = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

/**
 * The lockup's place in the top strip, when the filters that hold it have
 * stepped aside: it fades in as they ease away, rather than doubling them.
 */
export const StripLockup = styled('div')({
  animation: `${arrive} 320ms ease both`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

/**
 * Wraps rather than running under the view toggle: its room is whatever lies
 * between the toggle (`--toggle-x`, set by the page) and the column's right
 * edge. The column's own left edge on the page is `--strip-left`: the filters'
 * edge on Explore, zero wherever the column starts at the page's edge.
 */
export const StripLinks = styled('div')({
  flex: '1 1 0',
  minWidth: 0,
  marginLeft: 'auto',
  maxWidth: `calc(100% + var(--strip-left, 0px) + ${STRIP_X}px - var(--toggle-x) - var(--toggle-w) / 2 - ${TOGGLE_GAP}px)`,
  // Measured, so the preview's account links can tell when they do not fit.
  containerType: 'inline-size',
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
  alignItems: 'center',
  columnGap: 24,
  rowGap: 4,
  whiteSpace: 'nowrap',
});

/**
 * Log in / Sign up, on localhost only. Beside an open right sidebar below
 * about 1120px there is no room for them clear of the view toggle, and they
 * step aside rather than run under it.
 */
export const PreviewLinks = styled('span')({
  display: 'contents',
  '@container (max-width: 119.95px)': { display: 'none' },
});

/**
 * The Account link, on every host. One word needs less room than Log in /
 * Sign up did, so it steps aside only where even that would run under the
 * view toggle: beside an open right sidebar on the narrowest desktops.
 */
export const AccountLinkSpot = styled('span')({
  display: 'contents',
  '@container (max-width: 69.95px)': { display: 'none' },
});

/** A sidebar's own top: the lockup on the left, the two links on the right. */
export const SidebarTop = styled('div')({
  flexShrink: 0,
  height: TOP_STRIP_H,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  whiteSpace: 'nowrap',
  containerType: 'inline-size',
});

/** A fixed width rather than a drag handle: nothing in it scales the way the filters do. */
export const RightSidebar = styled('aside')({
  flexShrink: 0,
  width: 'clamp(300px, 20vw, 380px)',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  background: SIDEBAR_GROUND,
});

export const RIGHT_SIDEBAR_X = 32;

export const RightSidebarBody = styled('div')({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  padding: `4px ${RIGHT_SIDEBAR_X}px 32px`,
});

/** Where the two sidebar toggles sit: the top corners of the centre column, level with the results bar. */
export const ToggleSpot = styled('div', { shouldForwardProp: (p) => p !== '$side' })<{
  $side: 'left' | 'right';
}>(({ $side }) => ({
  position: 'absolute',
  top: 8,
  [$side]: 10,
  // Above the results bar (7) and the fade under it (5).
  zIndex: 8,
}));
